import { supabase } from './supabase';
import { getCurrentTenantId } from './tenantUtils';

// ECA Data Service for managing extracurricular activities
export const ecaService = {
  // Get all ECA categories
  getCategories: async () => {
    const { data, error } = await supabase
      .from('eca_categories')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data;
  },

  // Get all ECA activities with category information for current tenant
  getActivities: async (tenantId = null, filters = {}) => {
    let query = supabase
      .from('eca_activities')
      .select(`
        *,
        eca_categories (
          id,
          name,
          name_en,
          color_code
        )
      `)
      .order('name');

    // Filter by tenant if provided
    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    // Apply other filters
    if (filters.category_id) {
      query = query.eq('category_id', filters.category_id);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.age_group) {
      query = query.eq('age_group', filters.age_group);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get ECA activity by ID
  getActivityById: async (id) => {
    const { data, error } = await supabase
      .from('eca_activities')
      .select(`
        *,
        eca_categories (
          id,
          name,
          name_en,
          color_code
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Get student's ECA memberships
  getStudentMemberships: async (studentId) => {
    const { data, error } = await supabase
      .from('eca_student_memberships')
      .select(`
        *,
        eca_activities (
          id,
          name,
          name_en,
          description,
          schedule,
          venue,
          image_url,
          eca_categories (
            name,
            name_en,
            color_code
          )
        )
      `)
      .eq('student_id', studentId)
      .order('join_date', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get ECA memberships for a specific activity
  getActivityMemberships: async (activityId) => {
    const { data, error } = await supabase
      .from('eca_student_memberships')
      .select(`
        *,
        saas_students (
          id,
          name,
          student_code,
          class_id
        )
      `)
      .eq('eca_id', activityId)
      .order('join_date', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get ECA memberships for current tenant
  getTenantMemberships: async (tenantId) => {
    const { data, error } = await supabase
      .from('eca_student_memberships')
      .select(`
        *,
        eca_activities (
          id,
          name,
          name_en,
          description,
          schedule,
          venue,
          status,
          max_capacity,
          current_enrollment,
          fee,
          tenant_id
        ),
        saas_students (
          id,
          name,
          student_code,
          class_id,
          tenant_id
        )
      `)
      .eq('eca_activities.tenant_id', tenantId)
      .order('join_date', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get ECA statistics for current tenant
  getTenantStats: async (tenantId) => {
    const { data, error } = await supabase
      .from('eca_activities')
      .select(`
        id,
        name,
        category_id,
        max_capacity,
        current_enrollment,
        status
      `)
      .eq('tenant_id', tenantId);

    if (error) throw error;
    
    // Calculate statistics
    const stats = {
      totalActivities: data.length,
      totalCapacity: data.reduce((sum, activity) => sum + activity.max_capacity, 0),
      totalEnrolled: data.reduce((sum, activity) => sum + (activity.current_enrollment || 0), 0),
      byCategory: {}
    };

    data.forEach(activity => {
      if (!stats.byCategory[activity.category_id]) {
        stats.byCategory[activity.category_id] = {
          count: 0,
          capacity: 0,
          enrolled: 0
        };
      }
      stats.byCategory[activity.category_id].count++;
      stats.byCategory[activity.category_id].capacity += activity.max_capacity;
      stats.byCategory[activity.category_id].enrolled += (activity.current_enrollment || 0);
    });

    return stats;
  },

  // Get current tenant ID
  getCurrentTenantId: () => {
    return getCurrentTenantId();
  },

  // Get ECA performance statistics for students
  getStudentECAPerformance: async (tenantId) => {
    const { data, error } = await supabase
      .from('eca_student_performance')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('total_eca_joined', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get ECA category statistics
  getECACategoryStats: async (tenantId) => {
    const { data, error } = await supabase
      .from('eca_category_statistics')
      .select('*');

    if (error) throw error;
    return data;
  },

  // Get detailed student ECA information
  getStudentECADetailed: async (tenantId, studentId = null) => {
    let query = supabase
      .from('student_eca_detailed')
      .select('*')
      .eq('tenant_id', tenantId);

    if (studentId) {
      query = query.eq('student_id', studentId);
    }

    const { data, error } = await query.order('student_name', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Get ECA summary for dashboard
  getECASummary: async (tenantId) => {
    const [activities, memberships, performance, categoryStats] = await Promise.all([
      this.getActivities(tenantId),
      this.getTenantMemberships(tenantId),
      this.getStudentECAPerformance(tenantId),
      this.getECACategoryStats(tenantId)
    ]);

    return {
      totalActivities: activities.length,
      totalStudents: memberships.length,
      totalCategories: categoryStats.length,
      topActivities: activities
        .sort((a, b) => (b.current_enrollment || 0) - (a.current_enrollment || 0))
        .slice(0, 5),
      topStudents: performance
        .sort((a, b) => (b.total_eca_joined || 0) - (a.total_eca_joined || 0))
        .slice(0, 5),
      categoryBreakdown: categoryStats
    };
  }
};